import { cn } from '@myapp/ui';
import { Button } from '@myapp/ui';
import { 
  Package, 
  Search, 
  MapPin, 
  ShoppingBag,
  ChefHat,
  Heart,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

interface EmptyStateProps {
  className?: string;
  icon?: React.ReactNode;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export function EmptyState({ 
  className,
  icon,
  title, 
  description, 
  action 
}: EmptyStateProps) {
  return (
    <div className={cn(
      "flex flex-col items-center justify-center py-16 px-4 text-center",
      className
    )}>
      {icon && (
        <div className="mb-4 text-muted-foreground">
          {icon}
        </div>
      )}
      <h3 className="text-lg font-semibold text-foreground mb-2">
        {title}
      </h3>
      <p className="text-sm text-muted-foreground max-w-sm mb-6">
        {description}
      </p>
      {action && (
        <Button onClick={action.onClick} variant="outline">
          {action.label}
        </Button>
      )}
    </div>
  );
}

// No Products Empty State
export function NoProductsEmptyState({ 
  onReset,
  className 
}: { 
  onReset?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      className={className}
      icon={<Package className="w-12 h-12" />}
      title="상품이 없습니다"
      description="선택하신 지역에는 아직 등록된 특산물이 없습니다. 다른 지역을 선택해보세요."
      action={onReset ? {
        label: "전체 지역 보기",
        onClick: onReset
      } : undefined}
    />
  );
}

// No Search Results Empty State
export function NoSearchResultsEmptyState({ 
  query,
  onClear,
  className 
}: { 
  query?: string;
  onClear?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      className={className}
      icon={<Search className="w-12 h-12" />}
      title="검색 결과가 없습니다"
      description={
        query 
          ? `'${query}'에 대한 검색 결과를 찾을 수 없습니다. 다른 검색어를 시도해보세요.`
          : "검색 조건과 일치하는 상품이 없습니다."
      }
      action={onClear ? {
        label: "검색 초기화",
        onClick: onClear
      } : undefined}
    />
  );
}

// No Region Selected Empty State
export function NoRegionSelectedEmptyState({ 
  onSelectRegion,
  className 
}: { 
  onSelectRegion?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      className={className}
      icon={<MapPin className="w-12 h-12" />}
      title="지역을 선택해주세요"
      description="강원도 지도에서 원하시는 지역을 클릭하면 해당 지역의 특산물을 확인할 수 있습니다."
      action={onSelectRegion ? {
        label: "지도 보기",
        onClick: onSelectRegion
      } : undefined}
    />
  );
}

// Empty Cart State
export function EmptyCartState({ 
  onShop,
  className 
}: { 
  onShop?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      className={className}
      icon={<ShoppingBag className="w-12 h-12" />}
      title="장바구니가 비어있습니다"
      description="강원도의 신선한 특산물을 장바구니에 담아보세요."
      action={onShop ? {
        label: "쇼핑 시작하기",
        onClick: onShop
      } : undefined}
    />
  );
}

// No Recipes Empty State
export function NoRecipesEmptyState({ 
  onGenerateRecipe,
  className 
}: { 
  onGenerateRecipe?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      className={className}
      icon={<ChefHat className="w-12 h-12" />}
      title="아직 레시피가 없습니다"
      description="AI가 이 특산물로 만들 수 있는 맛있는 레시피를 추천해드립니다."
      action={onGenerateRecipe ? {
        label: "AI 레시피 생성",
        onClick: onGenerateRecipe
      } : undefined}
    />
  );
}

// No Favorites Empty State
export function NoFavoritesEmptyState({ 
  onBrowse,
  className 
}: { 
  onBrowse?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      className={className}
      icon={<Heart className="w-12 h-12" />}
      title="즐겨찾기가 비어있습니다"
      description="마음에 드는 특산물을 즐겨찾기에 추가하고 나중에 쉽게 찾아보세요."
      action={onBrowse ? {
        label: "특산물 둘러보기",
        onClick: onBrowse
      } : undefined}
    />
  );
}

// Network Error State
export function NetworkErrorState({ 
  onRetry,
  className 
}: { 
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      className={className}
      icon={<AlertCircle className="w-12 h-12 text-destructive" />}
      title="연결할 수 없습니다"
      description="네트워크 연결을 확인하고 다시 시도해주세요."
      action={onRetry ? {
        label: "다시 시도",
        onClick: onRetry
      } : undefined}
    />
  );
}

// Loading Error State
export function LoadingErrorState({ 
  error,
  onRetry,
  className 
}: { 
  error?: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div className={cn(
      "flex flex-col items-center justify-center py-16 px-4 text-center",
      className
    )}>
      <div className="mb-4 text-destructive">
        <AlertCircle className="w-12 h-12" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">
        불러오기 실패
      </h3>
      <p className="text-sm text-muted-foreground max-w-sm mb-6">
        {error || "데이터를 불러오는 중 문제가 발생했습니다."}
      </p>
      {onRetry && (
        <Button onClick={onRetry} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          다시 시도
        </Button>
      )}
    </div>
  );
}